import { createContext, useEffect, useState } from "react";
import {
  ReadLayerQuery,
  ReadLayerQueryFilter,
  ReadLayerResult,
  searchReadLayer
} from "api/services/readLayerService";

import { GenericError } from "components/GenericError";
import { useQuery } from "@tanstack/react-query";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReadLayerData<T = any> = {
  data?: ReadLayerResult<T>;
  query: Required<ReadLayerQuery>;
  updateQuery: (changes: Record<string, unknown>) => void;
  addFilter: (filter: ReadLayerQueryFilter, allowMultiple?: boolean) => void;
  setFilters: (filters: ReadLayerQueryFilter[]) => void;
  isLoading: boolean;
  refetch: () => void;
  resetCount: number;
  reset: () => void;
};

interface ReadQueryContextProps {
  defaultQuery: Required<ReadLayerQuery>;
  onUpdate: (query: ReadLayerQuery) => void;
  filterRewrite?: (filter: ReadLayerQueryFilter) => ReadLayerQueryFilter;
  children: React.ReactNode;
  disableQuery: boolean;
  subscribeToPropUpdates: boolean;
}

export const ReadLayerContext = createContext<ReadLayerData | null>(null);

export const ReadLayerQueryContext = ({
  defaultQuery,
  children,
  onUpdate,
  filterRewrite,
  disableQuery,
  subscribeToPropUpdates
}: ReadQueryContextProps) => {
  const rewriteQuery = (query: Required<ReadLayerQuery>) => {
    query = { ...query };

    if (filterRewrite) query.filters = query.filters.map(x => filterRewrite(x));

    return query;
  };

  const [query, setQuery] = useState<Required<ReadLayerQuery>>(
    rewriteQuery(defaultQuery)
  );

  useEffect(() => {
    if (!subscribeToPropUpdates) return;
    setQuery(rewriteQuery(defaultQuery));
  }, [subscribeToPropUpdates, defaultQuery]);

  const [resetCount, setResetCount] = useState(0);

  const { data, isLoading, error, refetch } = useQuery(
    ["searchReadLayer", query],
    () => searchReadLayer(query),
    { cacheTime: 0, enabled: !disableQuery }
  );

  const updateQuery = (changes: Record<string, unknown>) => {
    if (
      changes.searchTerm === query.searchTerm &&
      changes.searchTermColumns === query.searchTermColumns
    ) {
      refetch();
    } else {
      const newQuery = { ...query, ...changes };
      onUpdate(newQuery);
      setQuery(rewriteQuery(newQuery));
    }
  };

  const addFilter = (filter: ReadLayerQueryFilter, allowMultiple?: boolean) => {
    const filters = [
      ...query.filters.filter(f => allowMultiple || f.column !== filter.column),
      filter
    ];

    if (filter.value || filter.fromDateTimeUtc || filter.values)
      updateQuery({ filters, page: 1 });
    else {
      const filtersWithoutRemovedFilter = filters.filter(
        f => f.column !== filter.column
      );

      updateQuery({ filters: filtersWithoutRemovedFilter, page: 1 });
    }
  };

  const setFilters = (filters: ReadLayerQueryFilter[]) => {
    updateQuery({ filters, page: 1 });
  };

  const reset = () => {
    updateQuery({
      filters: defaultQuery.filters,
      page: 1,
      searchTerm: ""
    });
    setResetCount(resetCount + 1);
  };

  return (
    <ReadLayerContext.Provider
      value={{
        data,
        query,
        updateQuery,
        addFilter,
        isLoading,
        refetch,
        setFilters,
        resetCount,
        reset
      }}
    >
      {Boolean(error) && <GenericError />}
      {children}
    </ReadLayerContext.Provider>
  );
};

export default ReadLayerQueryContext;
